import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Typography,
  CircularProgress
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { importProfileFromDevice } from '../../utilities/mezzanineApi';
import { useMezzanine } from '../../context/MezzanineContext';

const useStyles = makeStyles((theme) => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
}));

const PROFILE_TYPES = [
  { value: 'video', label: 'Video' },
  { value: 'audio', label: 'Audio' },
  { value: 'color', label: 'Color' },
  { value: 'vanc', label: 'VANC' },
];

const ImportProfileForm = ({ open, onClose, onSuccess, onNotify }) => {
  const classes = useStyles();
  const { devices, devicesLoading } = useMezzanine();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    device_id: '',
    profile_type: '',
    label: '',
  });
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.device_id) {
      newErrors.device_id = 'Device is required';
    }
    if (!formData.profile_type) {
      newErrors.profile_type = 'Profile type is required';
    }
    if (!formData.label || formData.label.trim() === '') {
      newErrors.label = 'Profile label is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await importProfileFromDevice(formData);
      
      if (onNotify) {
        onNotify({ message: 'Profile imported successfully', severity: 'success' });
      }
      onSuccess && onSuccess();
      handleClose();
    } catch (err) {
      if (onNotify) {
        onNotify({ message: `Failed to import profile: ${err.message}`, severity: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      device_id: '',
      profile_type: '',
      label: '',
    });
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle style={{ borderBottom: '1px solid #eee', padding: '16px 20px' }}>
        Import Profile from Device
      </DialogTitle>
      <DialogContent style={{ padding: '20px' }}>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: '8px', fontSize: '12px' }}>
          Select a device and profile type. The profile configuration will be pulled from the device and saved to the database.
        </Typography>
        <div className={classes.form}>
          <TextField
            select
            label="Device"
            variant="outlined"
            size="small"
            value={formData.device_id}
            onChange={handleChange('device_id')}
            error={!!errors.device_id}
            helperText={errors.device_id}
            required
            fullWidth
            disabled={devicesLoading}
            InputProps={{
              style: { fontSize: '12px' }
            }}
            InputLabelProps={{
              style: { fontSize: '12px' }
            }}
          >
            {devices.map((device) => (
              <MenuItem key={device.device_id} value={device.device_id} style={{ fontSize: '12px' }}>
                {device.device_id} ({device.data_center})
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Profile Type"
            variant="outlined"
            size="small"
            value={formData.profile_type}
            onChange={handleChange('profile_type')}
            error={!!errors.profile_type}
            helperText={errors.profile_type}
            required
            fullWidth
            InputProps={{
              style: { fontSize: '12px' }
            }}
            InputLabelProps={{
              style: { fontSize: '12px' }
            }}
          >
            {PROFILE_TYPES.map((type) => (
              <MenuItem key={type.value} value={type.value} style={{ fontSize: '12px' }}>
                {type.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Profile Label"
            variant="outlined"
            size="small"
            value={formData.label}
            onChange={handleChange('label')}
            error={!!errors.label}
            helperText={errors.label}
            required
            fullWidth
            placeholder="e.g., 1080p-HEVC-SDR-50fps"
            InputProps={{
              style: { fontSize: '12px' }
            }}
            InputLabelProps={{
              style: { fontSize: '12px' }
            }}
          />
        </div>
      </DialogContent>
      <DialogActions style={{ borderTop: '1px solid #eee', padding: '12px 20px' }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          style={{ backgroundColor: loading ? '#ccc' : '#ff6f00', color: 'white' }}
        >
          {loading ? <CircularProgress size={20} style={{ color: 'white' }} /> : 'Import'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportProfileForm;
